import { createRouter, createWebHistory } from "vue-router";
import animaxRoutes from "@animax/router";
import habitatsRoutes from "@habitats/router";
import profileRoutes from "@profile/router";
import { idDepuisChemin } from "@/modules/scan/jeton";

const Default = () => import("@/views/Default.vue");
const Landing = () => import("@/views/Landing.vue");
const Merci = () => import("@/views/Merci.vue");

const routes = [
  {
    // The regex ensures that routes with tokens containing characters considered as special (e.g. # or ?) are considered part of the :id and match the route.
    // /tk/:id est l'URL imprimée sur les jetons physiques (cf. CLAUDE.md, non-négociable).
    // Depuis l'auto-anonymous-auth, on redirige vers scan-result-route qui gère
    // tout le flow (auth + claim + UI selon état). ScanResult détecte l'entrée
    // externe via route.redirectedFrom pour préserver entry_point=external dans GA4.
    // Le redirect reconstruit l'id depuis fullPath si l'URL contient # ou ?
    // (le browser les parse en hash/query, donc to.params.id se retrouve vide
    // ou tronqué — cf. workaround historique). Le beforeEach gère le même
    // cas pour les arrivées directes sur /scan/.
    path: "/tk/:id(.*)?",
    name: "external-scan-route",
    redirect: () => {
      // window.location est la source de vérité : pour des URLs avec # ou ?,
      // Vue Router peut perdre le segment dans to.fullPath/to.params (le #
      // est parsé en hash par le browser avant que Vue Router ne voie l'URL).
      // On reconstruit l'id complet en concaténant pathname + search + hash.
      const { pathname, search, hash } = window.location;
      const id = idDepuisChemin(pathname, search, hash);
      // /tk/ sans identifiant : page blanche avant (paramètre requis manquant).
      if (!id) return { name: "landing-route", hash: "", query: {} };
      // hash + query forcés à vide : Vue Router 4 forward le hash/query du
      // source par défaut, ce qui dupliquerait le # dans l'URL cible.
      return { name: "scan-result-route", params: { id }, hash: "", query: {} };
    },
  },
  {
    path: "/",
    name: "app-route",
    component: Default,
    children: [
      // Page d'accueil à la racine (la Collection devient une sous-page /animax).
      {
        path: "",
        name: "landing-route",
        component: Landing,
        // hideMenu : pas de header/footer sur cette page (cf. Default.vue).
        // Mécanisme modulaire : ajouter `hideMenu: true` au meta de n'importe
        // quelle route pour la rendre plein écran sans le menu.
        meta: { title: "Accueil", hideMenu: true, fullBleed: true },
      },
      {
        // Page de confirmation post-paiement (Stripe y redirige).
        path: "merci",
        name: "merci-route",
        component: Merci,
        meta: { title: "Merci", hideMenu: true },
      },
      ...animaxRoutes,
      ...habitatsRoutes,
      ...profileRoutes,
    ],
  },
  { path: "/:catchAll(.*)", redirect: { name: "landing-route" } },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach((to, _from, next) => {
  const title = to.meta.title;
  const id = to.params.id;

  if (title) {
    document.title = title;
  }
  if (id) {
    document.title = title + id;
  }

  if (
    (to.path.startsWith("/tk/") || to.path.includes("/scan/")) &&
    (to.hash || Object.keys(to.query).length)
  ) {
    // Workaround for generated token IDs that start with '#' or contain '?'
    // as they are respectively interpreted as hash or query by Vue router.
    const tokenId = to.fullPath.replace(to.path.startsWith("/tk/") ? "/tk/" : /^.+\/scan\//, "");
    next({ name: to.name, params: { id: tokenId } });
  } else {
    next();
  }
});

export default router;
